import { useEffect, useState } from "react"
import { boundaryCRUD } from "../../../actions/boundary"
import { selector } from "../../../api"
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { toast } from "react-toastify"
import { Loading } from "../../constant"

const islandSchema = Yup.object().shape({
  name: Yup.string().required('Island name is required'),
  code: Yup.string(),
  initials: Yup.string(),
  postcode: Yup.string(),
  centroid_lat: Yup.number().nullable().min(-90, 'Latitude must be >= -90').max(90, 'Latitude must be <= 90'),
  centroid_lng: Yup.number().nullable().min(-180, 'Longitude must be >= -180').max(180, 'Longitude must be <= 180'),
  island_polygon: Yup.string().nullable(),
})

export const EditIsland = (props) => {
  const { dataId, setIndex, index } = props
  const user = selector.User()
  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)

  const initialValues = {
    name: '',
    code: '',
    initials: '',
    postcode: '',
    centroid_lat: '',
    centroid_lng: '',
    centroid_coords: '',
    island_polygon: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: islandSchema,
    onSubmit: (values, { setSubmitting }) => {
      const { name, code, initials, postcode, centroid_lat, centroid_lng, island_polygon } = values
      const data = {
        name,
        code,
        initials,
        postcode: postcode || null,
        centroid_lat: centroid_lat !== '' ? parseFloat(centroid_lat) : null,
        centroid_lng: centroid_lng !== '' ? parseFloat(centroid_lng) : null,
        island_polygon: island_polygon || null,
      }
      setLoading(true)
      setTimeout(() => {
        boundaryCRUD.updateIsland(dataId, data).then(() => {
          toast.success('Island has been updated.', { autoClose: 7000 })
          setIndex(index + 1)
          setSubmitting(false)
          setLoading(false)
        }).catch((error) => {
          toast.error(error.message || 'Failed to update island', { autoClose: 7000 })
          setSubmitting(false)
          setLoading(false)
        })
      }, 500)
    }
  })

  useEffect(() => {
    if (!dataId) return;

    setDataLoading(true)
    setTimeout(() => {
      boundaryCRUD.loadIslandById(dataId).then((resp) => {
        formik.setFieldValue('name', resp.data.name || '')
        formik.setFieldValue('code', resp.data.code || '')
        formik.setFieldValue('initials', resp.data.initials || '')
        formik.setFieldValue('postcode', resp.data.postcode || '')
        formik.setFieldValue('centroid_lat', resp.data.centroid_lat !== null ? resp.data.centroid_lat : '')
        formik.setFieldValue('centroid_lng', resp.data.centroid_lng !== null ? resp.data.centroid_lng : '')
        if (resp.data.centroid_lat != null && resp.data.centroid_lng != null) {
          formik.setFieldValue('centroid_coords', `${resp.data.centroid_lat}, ${resp.data.centroid_lng}`)
        }
        formik.setFieldValue('island_polygon', resp.data.island_polygon || '')
      }).finally(() => {
        setDataLoading(false)
      })
    }, 500)
  }, [dataId])

  return (
    <>
      <div className="modal fade editModal" tabIndex="-1" role="dialog" aria-labelledby="editIslandModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editIslandModalLabel">Edit Island</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <hr />
            <form onSubmit={formik.handleSubmit}>
              {dataLoading ? <Loading /> :
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">Island Name <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" {...formik.getFieldProps('name')} placeholder="Island Name" />
                        {formik.touched.name && formik.errors.name && (
                          <div className='fw-light h6 mt-1' style={{ fontSize: '15px' }}>
                            <span role='alert' className='text-danger'>{formik.errors.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="code" className="form-label">Island Code</label>
                        <input type="text" className="form-control" {...formik.getFieldProps('code')} placeholder="Island Code" />
                      </div>
                    </div>
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="initials" className="form-label">Initials</label>
                        <input type="text" className="form-control" {...formik.getFieldProps('initials')} placeholder="Initials" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="postcode" className="form-label">Postcode</label>
                        <input type="text" className="form-control" {...formik.getFieldProps('postcode')} placeholder="Postcode" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <h6 className="text-muted mb-3">Island Centroid</h6>
                    </div>
                    <div className="col-md-12 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="centroid_coords" className="form-label">Centroid (Latitude, Longitude)</label>
                        <input
                          type="text"
                          id="centroid_coords"
                          className="form-control"
                          value={formik.values.centroid_coords}
                          onChange={(e) => {
                            const raw = e.target.value
                            formik.setFieldValue('centroid_coords', raw)
                            const parts = raw.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean)
                            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                              formik.setFieldValue('centroid_lat', parts[0])
                              formik.setFieldValue('centroid_lng', parts[1])
                            } else {
                              formik.setFieldValue('centroid_lat', '')
                              formik.setFieldValue('centroid_lng', '')
                            }
                          }}
                          placeholder="e.g. -6.1659, 39.2026"
                        />
                        <small className="text-muted">
                          Paste as <strong>Latitude, Longitude</strong> (e.g. -6.1659, 39.2026) — same order as Google Maps.
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Island Polygon</label>
                        <textarea
                          id="island_polygon"
                          {...formik.getFieldProps('island_polygon')}
                          className="form-control"
                          rows="4"
                          placeholder="Paste GeoJSON or WKT polygon data here..."
                        />
                        <small className="text-muted">Paste MultiPolygon geometry in GeoJSON or WKT format</small>
                      </div>
                    </div>
                  </div>
                </div>
              }
              <hr />
              <div className="modal-footer">
                <a href="javascript:void(0);" className="btn btn-soft-danger fw-medium" data-bs-dismiss="modal"><i className="ri-close-line me-1 align-middle"></i> Close</a>
                <button type='submit' className="btn btn-soft-success" disabled={formik.isSubmitting || !formik.isValid}>
                  {!loading && <span className='indicator-label'><i className="las la-save la-lg"></i> Save </span>}
                  {loading && (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
